import { useRef } from "react";
import { motion, useScroll, useTransform } from "framer-motion";
import { ArrowUpRight, ArrowRight, Activity, TrendingUp, Boxes, Truck } from "lucide-react";
import { IMAGES } from "@/data";

const LINES = ["TAKE CONTROL", "OF YOUR", "WAREHOUSE"];

const lineVariant = {
  hidden: { y: "110%" },
  show: (i) => ({
    y: "0%",
    transition: { duration: 0.9, delay: 0.15 + i * 0.12, ease: [0.22, 1, 0.36, 1] },
  }),
};

export const Hero = ({ onBookDemo, onExplore }) => {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start start", "end start"] });
  const imgY = useTransform(scrollYProgress, [0, 1], [0, 140]);
  const dashY = useTransform(scrollYProgress, [0, 1], [0, -60]);

  return (
    <section id="top" ref={ref} className="relative min-h-screen overflow-hidden bg-white pt-28 grain">
      <motion.div style={{ y: imgY }} className="pointer-events-none absolute inset-0 -z-10">
        <img src={IMAGES.aisle} alt="warehouse aisle" className="h-full w-full object-cover opacity-[0.07]" />
        <div className="absolute inset-0 bg-gradient-to-b from-white/60 via-white/85 to-white" />
      </motion.div>
      <div className="pointer-events-none absolute inset-0 -z-10 bg-grid opacity-70" />

      <div className="mx-auto grid max-w-[1400px] items-end gap-12 px-6 pb-20 pt-10 lg:grid-cols-[1.05fr_0.95fr] lg:px-10">
        <div>
          <motion.div
            initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: 0.1 }}
            className="mb-7 flex items-center gap-3 font-mono-p text-xs uppercase tracking-[0.25em] text-volt"
          >
            <span className="h-2 w-2 animate-pulse rounded-full bg-volt" />
            Warehouse Management. Reimagined.
          </motion.div>

          <h1 className="font-display text-[15vw] font-black leading-[0.85] text-slate-900 sm:text-[10vw] lg:text-[6.6vw]">
            {LINES.map((line, i) => (
              <span key={line} className="block overflow-hidden">
                <motion.span custom={i} variants={lineVariant} initial="hidden" animate="show" className="block">
                  {i === 2 ? <span className="text-stroke-volt">{line}</span> : line}
                </motion.span>
              </span>
            ))}
          </h1>

          <motion.p
            initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.8, duration: 0.7 }}
            className="mt-8 max-w-xl text-base leading-relaxed text-slate-500 sm:text-lg"
          >
            Know what you have. Know where it is. Know what's coming. Know what needs to
            happen next. One platform for inventory, operations, planning, production,
            pallets and 3PL.
          </motion.p>

          <motion.div
            initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.95, duration: 0.7 }}
            className="mt-10 flex flex-wrap items-center gap-4"
          >
            <button
              data-testid="hero-book-demo"
              onClick={onBookDemo}
              className="group flex items-center gap-2 bg-volt px-7 py-4 font-display text-base font-bold text-white transition-transform hover:-translate-y-0.5"
            >
              Book a Demo
              <ArrowUpRight className="h-5 w-5 transition-transform group-hover:translate-x-0.5 group-hover:-translate-y-0.5" strokeWidth={2.2} />
            </button>
            <button
              data-testid="hero-explore"
              onClick={onExplore}
              className="group flex items-center gap-2 border border-slate-300 px-7 py-4 font-display text-base font-bold text-slate-900 transition-colors hover:border-volt hover:text-volt"
            >
              Explore Stockaris
              <ArrowRight className="h-5 w-5 transition-transform group-hover:translate-x-1" strokeWidth={2.2} />
            </button>
          </motion.div>
        </div>

        <motion.div style={{ y: dashY }} className="relative">
          <HeroDashboard />
        </motion.div>
      </div>
    </section>
  );
};

const HeroDashboard = () => (
  <motion.div
    initial={{ opacity: 0, y: 60, rotateX: 8 }}
    animate={{ opacity: 1, y: 0, rotateX: 0 }}
    transition={{ delay: 0.6, duration: 1, ease: [0.22, 1, 0.36, 1] }}
    className="relative rounded-lg border border-slate-200 bg-white p-4 shadow-[0_40px_120px_-40px_rgba(47,68,232,0.35)]"
  >
    <div className="mb-4 flex items-center justify-between border-b border-slate-200 pb-3">
      <div className="flex items-center gap-2 font-mono-p text-[11px] uppercase tracking-widest text-slate-400">
        <span className="h-2 w-2 rounded-full bg-volt" /> stockaris · live
      </div>
      <div className="flex gap-1.5">
        <span className="h-2 w-2 rounded-full bg-slate-200" />
        <span className="h-2 w-2 rounded-full bg-slate-200" />
        <span className="h-2 w-2 rounded-full bg-slate-200" />
      </div>
    </div>

    <div className="grid grid-cols-3 gap-3">
      {[
        { icon: Boxes, k: "SKUs live", v: "12,480", d: "+2.1%" },
        { icon: Truck, k: "Orders today", v: "1,204", d: "+8.4%" },
        { icon: Activity, k: "Pick accuracy", v: "99.6%", d: "+0.3%" },
      ].map((c) => (
        <div key={c.k} className="rounded-md border border-slate-200 bg-slate-50 p-3">
          <c.icon className="mb-3 h-4 w-4 text-volt" strokeWidth={1.6} />
          <div className="font-mono-p text-[10px] uppercase tracking-wider text-slate-400">{c.k}</div>
          <div className="font-display text-xl font-bold text-slate-900">{c.v}</div>
          <div className="font-mono-p text-[10px] text-volt">{c.d}</div>
        </div>
      ))}
    </div>

    <div className="mt-3 rounded-md border border-slate-200 bg-slate-50 p-4">
      <div className="mb-3 flex items-center justify-between">
        <span className="font-mono-p text-[10px] uppercase tracking-wider text-slate-400">Throughput · 24h</span>
        <TrendingUp className="h-4 w-4 text-volt" strokeWidth={1.6} />
      </div>
      <div className="flex h-24 items-end gap-1.5">
        {[38, 52, 44, 66, 58, 74, 62, 82, 70, 90, 78, 96].map((h, i) => (
          <motion.div
            key={i}
            initial={{ height: 0 }}
            animate={{ height: `${h}%` }}
            transition={{ delay: 1 + i * 0.05, duration: 0.5 }}
            className={`flex-1 rounded-sm ${i % 3 === 0 ? "bg-volt" : "bg-slate-200"}`}
          />
        ))}
      </div>
    </div>

    <div className="mt-3 grid grid-cols-2 gap-3">
      <div className="rounded-md border border-slate-200 bg-slate-50 p-3">
        <div className="font-mono-p text-[10px] uppercase tracking-wider text-slate-400">Pallet variance</div>
        <div className="font-display text-lg font-bold text-destructive">-55 <span className="text-xs text-slate-400">CHEP</span></div>
      </div>
      <div className="rounded-md border border-slate-200 bg-slate-50 p-3">
        <div className="font-mono-p text-[10px] uppercase tracking-wider text-slate-400">Capacity used</div>
        <div className="font-display text-lg font-bold text-slate-900">82%</div>
      </div>
    </div>
  </motion.div>
);
